import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, Sparkles, Loader2, ChevronLeft, Star, Lock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';
import { getBookDetails } from '../services/mediaService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BooksRecommendationsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookDetails, setBookDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, recsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/recommendations/books`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setRecommendations(recsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/');
      } else {
        toast.error('Failed to load recommendations');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewBook = async (book) => {
    setSelectedBook(book);
    setLoadingDetails(true);
    try {
      const details = await getBookDetails(book.book_id);
      setBookDetails(details);
    } catch (error) {
      // Use basic book info if API fails
      setBookDetails({
        book_id: book.book_id,
        title: book.title,
        author: book.author,
        cover_image: book.cover_image,
        description: book.description || '',
        genres: book.genres || []
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const isUnlocked = recommendations?.unlocked;
  const readCount = recommendations?.read_count || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      <AppHeader user={user} showBack={true} backPath="/books" />
      
      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#f87676] rounded-xl flex items-center justify-center shadow-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-outfit text-2xl font-bold text-[#3c405b]">Book Recommendations</h1>
            <p className="text-[#64748B] text-sm">Personalized picks based on your reading history</p>
          </div>
        </div>

        {!isUnlocked ? (
          /* Locked State */
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-[#f87676]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-[#f87676]" />
              </div>
              <h2 className="text-xl font-bold text-[#3c405b] mb-2">Smart Recommendations Locked</h2>
              <p className="text-[#64748B] mb-6 max-w-md mx-auto">
                Read {10 - readCount} more books to unlock personalized recommendations based on your tastes.
              </p>
              <div className="w-full max-w-xs mx-auto h-3 bg-slate-100 rounded-full overflow-hidden mb-6">
                <div 
                  className="h-full bg-[#f87676] rounded-full transition-all"
                  style={{ width: `${(readCount / 10) * 100}%` }}
                />
              </div>
              <p className="text-sm text-[#94A3B8] mb-6">{readCount}/10 books read</p>
              <Button
                onClick={() => navigate('/books-discover')}
                className="bg-[#f87676] hover:bg-[#EF4444] text-white rounded-full"
              >
                Browse Books to Read
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Unlocked - Show Recommendations */
          <>
            {/* User's Top Genres */}
            {recommendations?.top_genres?.length > 0 && (
              <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="font-outfit text-lg text-[#3c405b]">Your Top Genres</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {recommendations.top_genres.map((genre, idx) => (
                      <span
                        key={genre}
                        className={`px-3 py-1 rounded-full text-sm ${
                          idx === 0 
                            ? 'bg-[#f87676] text-white' 
                            : 'bg-[#f87676]/10 text-[#f87676]'
                        }`}
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommended Books Grid */}
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <CardTitle className="font-outfit text-lg text-[#3c405b] flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#f87676]" />
                  Recommended For You
                </CardTitle>
                <p className="text-sm text-[#64748B]">Based on books you&apos;ve liked</p>
              </CardHeader>
              <CardContent>
                {recommendations?.recommendations?.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {recommendations.recommendations.map((book) => (
                      <div
                        key={book.book_id}
                        className="cursor-pointer group"
                        onClick={() => handleViewBook(book)}
                        data-testid={`rec-book-${book.book_id}`}
                      >
                        <div className="aspect-[2/3] rounded-lg overflow-hidden mb-2 shadow-md group-hover:shadow-lg transition-all">
                          {book.cover_image ? (
                            <img
                              src={book.cover_image}
                              alt={book.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-slate-100">
                              <BookOpen className="w-8 h-8 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <h3 className="font-medium text-sm text-[#3c405b] line-clamp-2 group-hover:text-[#f87676] transition-colors">
                          {book.title}
                        </h3>
                        <p className="text-xs text-[#64748B] line-clamp-1">{book.author}</p>
                        {book.average_rating && (
                          <div className="flex items-center gap-1 mt-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                            <span className="text-xs text-[#64748B]">{book.average_rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-[#64748B]">No recommendations available yet</p>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      Try reading more diverse books to get better recommendations
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Book Details Dialog */}
        {selectedBook && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedBook(null)}
          >
            <Card 
              className="w-full max-w-lg border-0 shadow-2xl bg-white max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <CardContent className="p-6">
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                  </div>
                ) : bookDetails && (
                  <>
                    <div className="flex gap-4 mb-4">
                      <div className="w-24 h-36 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
                        {bookDetails.cover_image ? (
                          <img
                            src={bookDetails.cover_image}
                            alt={bookDetails.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-8 h-8 text-slate-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h2 className="font-outfit font-bold text-xl text-[#3c405b]">{bookDetails.title}</h2>
                        <p className="text-[#64748B] mt-1">{bookDetails.author}</p>
                        {bookDetails.page_count > 0 && (
                          <p className="text-sm text-slate-600 mt-2">{bookDetails.page_count} pages</p>
                        )}
                        {bookDetails.average_rating && (
                          <div className="flex items-center gap-1 mt-2">
                            <Star className="w-4 h-4 text-yellow-400 fill-current" />
                            <span className="text-sm text-[#64748B]">{bookDetails.average_rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {bookDetails.genres?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {bookDetails.genres.slice(0, 5).map((genre) => (
                          <span
                            key={genre}
                            className="px-2 py-1 bg-[#f87676]/10 text-[#f87676] rounded-full text-xs"
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    )}

                    {bookDetails.description && (
                      <p className="text-sm text-slate-600 mb-4 line-clamp-6">{bookDetails.description}</p>
                    )}

                    <div className="flex gap-2">
                      <Button
                        onClick={() => navigate(`/books-discover?search=${encodeURIComponent(bookDetails.title)}`)}
                        className="flex-1 bg-[#f87676] hover:bg-[#EF4444] text-white rounded-full"
                      >
                        Find & Add to List
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setSelectedBook(null)}
                        className="rounded-full"
                      >
                        Close
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
